import { FlexCodeTransactionStatus, type HighnoteTransactionEvent, type Paidol } from 'API';
import { isReturned } from 'app/pages/overview/tabs/transactions/ReturnedStatusChip';
import { hasErpCodingUI } from 'app/util/paidolAccountType';
import { isSplitParent, type SplitAwareTransaction } from 'app/util/splitRows';
import { getSubmitBlockers, isSubmitBlocked } from 'util/submitRequirements';

// The ids that make a row coded, as the server's "uncoded" scenario reads them
// (mobile_actions_filters.py). A row coded only with equipment counts as coded.
const AGAVE_CODE_FIELDS = [
  'agaveGlCodeId',
  'agaveProjectId',
  'agaveCostCodeId',
  'agaveCostTypeId',
  'agaveVendorId',
  'agaveEquipmentId',
  'agaveEmCostCodeId',
  'agaveEmCostTypeId',
] as const;
const FLEX_CODE_FIELDS = [
  'flexGlCodeId',
  'flexCodeId',
  'flexCostCodeId',
  'flexCostTypeId',
  'flexVendorId',
] as const;

/**
 * Whether a transaction belongs on the cardholder's mobile Actions list. A returned one always
 * does and a reviewed, approved or bypassed one never does, nor does a split parent, which
 * nothing can change. Otherwise it stays while it is uncoded, while the submit rule would hold
 * its Submit back (SEE-1842), or while a fuel transaction is missing its fuel data.
 */
export const needsCardholderAction = (
  transaction: HighnoteTransactionEvent,
  paidol: Paidol | null | undefined,
  isMissingFuelData: boolean
): boolean => {
  if (isSplitParent(transaction as SplitAwareTransaction)) return false;
  if (isReturned(transaction.returnedStatus)) return true;

  const useAgave = hasErpCodingUI(paidol);
  const isDone = useAgave
    ? transaction.erpApprovalStatus || transaction.erpBypassStatus
    : transaction.flexCodeTransactionStatus === FlexCodeTransactionStatus.REVIEWED ||
      transaction.flexCodeTransactionStatus === FlexCodeTransactionStatus.APPROVED;
  if (isDone) return false;

  const isCoded = (useAgave ? AGAVE_CODE_FIELDS : FLEX_CODE_FIELDS).some((field) => !!transaction[field]);
  return !isCoded || isSubmitBlocked(getSubmitBlockers(transaction, paidol)) || isMissingFuelData;
};
